/**
 * Enumeration of generic phone states representing the different stages of any phone system provider.
 * These states abstract the underlying provider-specific states (e.g., Twilio, Bridge) to provide
 * a unified interface for phone operations across different implementations.
 */
const PHONE = {
  /**
   * Initial state when any phone system is starting up.
   * Used while the provider is being initialized but not yet ready to handle calls.
   *
   * Provider-specific behaviors:
   * - Twilio: Device is initializing but not yet registered
   * - Bridge: Typically skipped as no initialization is needed
   */
  CONNECTING: 'Connecting',

  /**
   * The system is attempting to establish a connection with the provider's services.
   * This is a transitional state between CONNECTING and READY.
   *
   * Provider-specific behaviors:
   * - Twilio: Device is registering with Twilio's servers
   * - Bridge: Not used as no registration is needed
   */
  REGISTERING: 'Registering',

  /**
   * The phone system is fully initialized and ready to handle calls.
   * This is the default operational state where:
   * - The system is properly configured
   * - No active calls are in progress
   * - New call operations can be initiated
   *
   * Provider-specific behaviors:
   * - Twilio: Device is registered and ready for direct calls
   * - Bridge: Default state, ready to initiate bridge calls
   */
  READY: 'Ready',

  /**
   * An incoming call is being received but not yet accepted.
   * Only applicable for providers that support real-time call reception.
   *
   * Provider-specific behaviors:
   * - Twilio: Incoming call received, waiting for user action
   * - Bridge: Not used as it doesn't support incoming calls
   */
  INCOMING: 'Incoming',

  /**
   * An outgoing call is being initiated and ringing on the recipient's end.
   * Represents the state where we're waiting for the other party to answer.
   *
   * Provider-specific behaviors:
   * - Twilio: Outgoing call is ringing on recipient's device
   * - Bridge: Call setup has been initiated through the bridge system
   */
  OUTGOING: 'Outgoing',

  /**
   * Call is established and both parties can communicate.
   * This represents an active, connected call session.
   *
   * Provider-specific behaviors:
   * - Twilio: Direct active call through the Twilio device
   * - Bridge: Call is connected and in progress
   */
  ESTABLISHED: 'Established',

  /**
   * The phone system is disconnected from the provider's services.
   * Indicates a loss of ability to handle calls through the current provider.
   *
   * Provider-specific behaviors:
   * - Twilio: Device becomes unregistered or is destroyed
   * - Bridge: Not typically used as it doesn't maintain persistent connections
   */
  OFFLINE: 'Offline',

  /**
   * An error state indicating a provider-specific failure.
   * The exact implications depend on the provider implementation.
   *
   * Provider-specific behaviors:
   * - Twilio: Non-critical device errors that don't require reset
   * - Bridge: API errors or call setup failures
   */
  ERROR: 'Error',
} as const;

/**
 * Type representing all possible phone states across different providers
 */
export type PhoneState = (typeof PHONE)[keyof typeof PHONE];

export default PHONE;
